import type { Shema } from '../api/odjemalec';

export type StatusNaloga = NonNullable<Shema['DelovniNalogPovzetek']['status']>;
export type VrstaPostavke = NonNullable<Shema['PostavkaNaloga']['vrsta']>;
export type VoziloStranke = Shema['VoziloStranke'];
type VoziloIzDokumenta = Shema['VoziloIzDokumenta'];

/** Statusi naloga v vrstnem redu dela (kot StatusNaloga na strežniku) z oznako in barvo značke. */
export const STATUSI: Record<StatusNaloga, { naziv: string; barva: string }> = {
  SPREJET: { naziv: 'Sprejet', barva: 'blue' },
  V_DELU: { naziv: 'V delu', barva: 'yellow' },
  CAKA_DELE: { naziv: 'Čaka dele', barva: 'orange' },
  CAKA_ODOBRITEV: { naziv: 'Čaka odobritev', barva: 'grape' },
  ZAKLJUCEN: { naziv: 'Zaključen', barva: 'green' },
  PREVZET: { naziv: 'Prevzet', barva: 'gray' },
};

/** Besedilo gumba za prehod naloga iz statusa {@code od} v {@code na} (»Začni delo«, »Zaključi«, »Znova odpri« …). */
export function oznakaPrehoda(od: StatusNaloga, na: StatusNaloga): string {
  switch (na) {
    case 'V_DELU':
      return od === 'SPREJET' ? 'Začni delo' : od === 'ZAKLJUCEN' ? 'Znova odpri' : 'Nadaljuj delo';
    case 'ZAKLJUCEN':
      return 'Zaključi';
    case 'PREVZET':
      return 'Stranka je prevzela vozilo';
    default:
      return STATUSI[na].naziv;
  }
}

/** Glavni naslednji korak iz statusa (poudarjen gumb na zaslonu naloga), ostali prehodi so navadni gumbi. */
export const GLAVNI_PREHOD: Partial<Record<StatusNaloga, StatusNaloga>> = {
  SPREJET: 'V_DELU',
  V_DELU: 'ZAKLJUCEN',
  CAKA_DELE: 'V_DELU',
  CAKA_ODOBRITEV: 'V_DELU',
  ZAKLJUCEN: 'PREVZET',
};

export const VRSTE_POSTAVK: { value: VrstaPostavke; label: string }[] = [
  { value: 'DELO', label: 'Delo' },
  { value: 'DEL', label: 'Del' },
  { value: 'MATERIAL', label: 'Material' },
];

/** Besedilo brez presledkov na začetku in koncu (prazno, če ga ni). */
export function obrezano(s: string | null | undefined): string {
  return (s ?? '').trim();
}

/** Velike črke za registrsko, VIN, znamko in model že med tipkanjem. */
export function velike(s: string): string {
  return s.toLocaleUpperCase('sl-SI');
}

/** Vozilo v eni vrstici: »LJ AB-123 · Škoda Octavia · 2018« (manjkajoči deli izpadejo). */
export function opisVozila(v: VoziloStranke | undefined | null): string {
  if (!v) {
    return '';
  }
  return [v.registrska, [v.znamka, v.model].filter(Boolean).join(' '), v.letnik, v.registrska ? null : v.vin]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Podatki, prebrani iz prometnega dovoljenja, v vozilo stranke: prebrano nadomesti vpisano, neprebrano ostane
 * (registrska, VIN, znamka, model = tip, letnik iz prve registracije, motor, gorivo, kilometri).
 */
export function izDokumenta(v: VoziloStranke, d: VoziloIzDokumenta | undefined): VoziloStranke {
  if (!d) {
    return v;
  }
  const leto = d.datumPrveReg ? Number(d.datumPrveReg.slice(0, 4)) : undefined;
  return {
    ...v,
    registrska: d.regSt ? velike(d.regSt) : v.registrska,
    vin: d.vin ? velike(d.vin) : v.vin,
    znamka: d.znamka ? velike(d.znamka) : v.znamka,
    model: d.tip ? velike(d.tip) : v.model,
    letnik: leto || v.letnik,
    motor: d.tipMotorja ?? v.motor,
    gorivo: d.dmvGorivo ?? v.gorivo,
    km: d.kilometri ?? v.km,
  };
}
